import React, { useCallback, useMemo } from 'react';
import ChevronDown from '@uber/base-icons/chevron-down';
import TrashCan from '@uber/base-icons/trash-can';
import { useStyletron } from 'baseui';
import { Button, KIND as BUTTON_KIND, SIZE as BUTTON_SIZE } from 'baseui/button';
import { StatefulMenu } from 'baseui/menu';
import { PLACEMENT, StatefulPopover } from 'baseui/popover';
import { DYNAMIC_ATTRIBUTE_TAG } from '../../../../labelling/constants';
import type { TrackSpanT } from '../../../types/frame-timeline-v4';
import { parseAttributeRowId } from '../../../utils/frame-timeline-v4';
import { useAttributeOptions } from './use-attribute-options';
type AttributeSpanPopoverContentPropsT = {
  /** Composite `${annotationId}:${questionId}` row id from the attribute descriptor. */
  attributeRowId: string;
  span: TrackSpanT;
  close: () => void;
  onUpdate?: (params: {
    annotationId: string;
    questionId: string;
    answer: {
      answers: string[];
      question: string;
      tags?: string[];
    };
    frameSpan: {
      start: number;
      end: number;
    };
  }) => void;
  onDelete?: (params: {
    annotationId: string;
    questionId: string;
    frameSpan: {
      start: number;
      end: number;
    };
  }) => void;
};
export const AttributeSpanPopoverContent = ({
  attributeRowId,
  span,
  close,
  onUpdate,
  onDelete
}: AttributeSpanPopoverContentPropsT) => {
  const [css, theme] = useStyletron();
  const {
    annotationId,
    questionId
  } = useMemo(() => parseAttributeRowId(attributeRowId), [attributeRowId]);
  const {
    options,
    isLoading,
    answerTemplate
  } = useAttributeOptions(annotationId, questionId);
  const menuItems = useMemo(() => {
    const values = new Set(options);
    if (span.value) {
      values.add(span.value);
    }
    return Array.from(values, value => ({
      id: value,
      label: value
    }));
  }, [options, span.value]);
  const handleValueChange = useCallback((nextValue: string) => {
    if (nextValue === span.value) {
      return;
    }
    const entry = answerTemplate ?? {
      answers: [],
      question: questionId,
      tags: [DYNAMIC_ATTRIBUTE_TAG]
    };
    onUpdate?.({
      annotationId,
      questionId,
      answer: {
        ...entry,
        answers: [nextValue]
      },
      frameSpan: {
        start: span.start,
        end: span.end
      }
    });
    close();
  }, [onUpdate, annotationId, questionId, span, answerTemplate, close]);
  const handleDelete = useCallback(() => {
    onDelete?.({
      annotationId,
      questionId,
      frameSpan: {
        start: span.start,
        end: span.end
      }
    });
    close();
  }, [onDelete, annotationId, questionId, span, close]);
  return <div className={css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.sizing.scale300
  })}>
      <StatefulPopover placement={PLACEMENT.bottomLeft} content={({
      close: closeMenu
    }) => <StatefulMenu items={menuItems} onItemSelect={({
      item
    }) => {
      handleValueChange(item.id);
      closeMenu();
    }} overrides={{
      List: {
        style: {
          width: 'max-content'
        }
      }
    }} />}>
        <Button kind={BUTTON_KIND.primary} size={BUTTON_SIZE.xSmall} isLoading={isLoading} endEnhancer={<ChevronDown size={12} />}>
          {span.value ?? 'Select'}
        </Button>
      </StatefulPopover>
      <Button kind={BUTTON_KIND.tertiary} size={BUTTON_SIZE.xSmall} startEnhancer={<TrashCan size={12} />} onClick={handleDelete}>
        Delete
      </Button>
    </div>;
};